// KOMPA 2.0 — Frontend RBAC permission matrix
// 6 roles: asesor, formador, supervisor, qa, gerente, superadmin

export const ROLES = ['asesor', 'formador', 'supervisor', 'qa', 'gerente', 'superadmin'];

export const ROLE_LABELS = {
  asesor: 'Asesor',
  formador: 'Formador',
  supervisor: 'Supervisor',
  qa: 'Quality Assurance',
  gerente: 'Gerente',
  superadmin: 'Superadmin',
};

// ─── Role groupings ───
export const READ_ONLY_ROLES = new Set(['formador', 'qa']);
export const ADMIN_ROLES = new Set(['gerente', 'superadmin']);
export const SUPERADMIN_ROLES = new Set(['superadmin']);
export const CAN_EDIT_LEADS = new Set(['asesor', 'supervisor', 'gerente', 'superadmin']);
export const CAN_REACTIVATE_USERS = new Set(['supervisor', 'gerente', 'superadmin']);

export const isReadOnly = (user) => user && READ_ONLY_ROLES.has(user.role);
export const isAdmin = (user) => user && ADMIN_ROLES.has(user.role);
export const isSuperadmin = (user) => user && SUPERADMIN_ROLES.has(user.role);
export const canEditLeads = (user) => user && CAN_EDIT_LEADS.has(user.role);
export const canReactivateUsers = (user) => user && CAN_REACTIVATE_USERS.has(user.role);
export const hasRole = (user, ...roles) => user && roles.includes(user.role);

// ─── Nav sections per role ───
// Each item: { id, label, icon, path, allowedRoles: [...] }
// A missing `allowedRoles` means allowed for all authenticated users.

export const NAV_MATRIX = [
  {
    title: 'Principal',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard', path: '/', allowedRoles: ['supervisor','formador','qa','gerente','superadmin'] },
    ],
  },
  {
    title: 'Ventas',
    items: [
      { id: 'pipeline',     label: 'Pipeline',      icon: 'GitBranch',  path: '/pipeline',     allowedRoles: ['asesor','supervisor','formador','qa','gerente','superadmin'] },
      { id: 'contactos',    label: 'Contactos',     icon: 'Users',      path: '/contactos',    allowedRoles: ['asesor','supervisor','formador','qa','gerente','superadmin'] },
      { id: 'pronosticos',  label: 'Pronosticos',   icon: 'TrendingUp', path: '/pronosticos',  allowedRoles: ['supervisor','gerente','superadmin'] },
      { id: 'territorios',  label: 'Territorios',   icon: 'Map',        path: '/territorios',  allowedRoles: ['supervisor','gerente','superadmin'] },
    ],
  },
  {
    title: 'Operaciones',
    items: [
      { id: 'agenda',         label: 'Agenda',             icon: 'Calendar',       path: '/agenda',         allowedRoles: ['asesor'] },
      { id: 'casos-exito',    label: 'Casos de Exito',     icon: 'Award',          path: '/casos-exito',    allowedRoles: ['formador','gerente','superadmin'] },
      { id: 'reasignacion',   label: 'Reasignacion',       icon: 'ArrowRightLeft', path: '/reasignacion',   allowedRoles: ['supervisor','gerente','superadmin'] },
      { id: 'aprobaciones',   label: 'Aprobaciones',       icon: 'CheckSquare',    path: '/aprobaciones',   allowedRoles: ['supervisor','gerente','superadmin'] },
      { id: 'auditoria',      label: 'Auditoria',          icon: 'ClipboardCheck', path: '/auditoria',      allowedRoles: ['qa','gerente','superadmin'] },
      { id: 'actividades',    label: 'Actividades',        icon: 'Activity',       path: '/actividades',    allowedRoles: ['asesor','supervisor','gerente','superadmin'] },
    ],
  },
  {
    title: 'Administracion',
    items: [
      { id: 'usuarios',  label: 'Usuarios',          icon: 'UserCog',   path: '/usuarios',  allowedRoles: ['gerente','superadmin'] },
      { id: 'aliados',   label: 'Aliados B2B',       icon: 'Store',     path: '/aliados',   allowedRoles: ['gerente','superadmin'] },
      { id: 'auditlog',  label: 'Historial Acceso',  icon: 'History',   path: '/auditlog',  allowedRoles: ['gerente','superadmin'] },
    ],
  },
  {
    title: 'Analisis',
    items: [
      { id: 'reportes',            label: 'Reportes',            icon: 'BarChart3',  path: '/reportes',            allowedRoles: ['supervisor','gerente','superadmin'] },
      { id: 'reportes-ejecutivos', label: 'Reportes Ejecutivos', icon: 'LineChart',  path: '/reportes-ejecutivos', allowedRoles: ['gerente','superadmin'] },
    ],
  },
];

// ─── Page → role guard for router ───
export const ROUTE_GUARDS = NAV_MATRIX.flatMap(s => s.items).reduce((acc, item) => {
  if (item.allowedRoles) acc[item.path] = item.allowedRoles;
  return acc;
}, {});

export const canAccessRoute = (user, path) => {
  if (!user) return false;
  const allowed = ROUTE_GUARDS[path];
  if (!allowed) return true;  // no guard = open to all authenticated
  return allowed.includes(user.role);
};

// Filter the nav matrix by the current user's role
export const navForUser = (user) => {
  if (!user) return [];
  return NAV_MATRIX
    .map(section => ({
      ...section,
      items: section.items.filter(it => !it.allowedRoles || it.allowedRoles.includes(user.role)),
    }))
    .filter(section => section.items.length > 0);
};
